/**
 * AppSuiteSection Organism Component
 * "Explore Our App Suite" section with product tabs, illustration, and CTA
 */

import React, { useState, useEffect } from "react";
import { storyblokEditable } from "@/lib/blocks";
import { Container } from "@/components/atoms/Container";
import { Text } from "@/components/atoms/Text";
import { Button } from "@/components/atoms/Button";
import { Image } from "@/components/atoms/Image";
import type { AppSuiteSectionBlok } from "@/types";

export interface AppSuiteSectionProps {
  blok: AppSuiteSectionBlok;
}

const renderAhaHeading = (text: string) => {
  const parts = text.split(/(_aha)/);
  return parts.map((part, i) => {
    if (part === "_aha") {
      return (
        <span key={i} className="normal-case">
          _aha
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

export const AppSuiteSection: React.FC<AppSuiteSectionProps> = ({ blok }) => {
  const { heading, subtitle, products, cta_text, cta_link } = blok;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  // Listen for product selection from ProductSubNav
  useEffect(() => {
    const handleSelectProduct = (e: Event) => {
      const { productKey } = (e as CustomEvent).detail;
      if (products) {
        const index = products.findIndex((p) => p.product_key === productKey);
        if (index >= 0) setActiveIndex(index);
      }
    };
    window.addEventListener('selectProduct', handleSelectProduct);
    return () => window.removeEventListener('selectProduct', handleSelectProduct);
  }, [products]);

  const activeProduct = products?.[activeIndex];

  const featureImageSrc = isDark
    ? activeProduct?.feature_image?.filename
    : activeProduct?.feature_image_light?.filename ||
      activeProduct?.feature_image?.filename;

  return (
    <section
      {...storyblokEditable(blok)}
      id={(blok as {anchor?: string}).anchor || undefined}
      className="scroll-mt-24 py-12 md:py-16 lg:py-20 xl:py-24 pb-0 md:pb-0 lg:pb-0 xl:pb-0 bg-theme-inset"
    >
      {/* Section Header */}
      <Container>
        <div className="flex flex-col gap-3 md:gap-4 text-center mb-8 md:mb-12">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-archivo-black leading-none text-black dark:text-primary uppercase whitespace-pre-line">
            {heading}
          </h2>
          {subtitle && (
            <p className="text-base md:text-xl leading-normal text-theme-text-secondary max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>
      </Container>

      {/* Product Tabs - Full Width Background, Centered Content */}
      {products && products.length > 0 && (
        <div className="w-full bg-theme-inset">
          <Container>
            <div
              className="grid"
              style={{ gridTemplateColumns: `repeat(${products.length}, 1fr)` }}
            >
              {products.map((product, index) => (
                <button
                  key={product._uid}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={`scroll-mt-24 
                    py-5 md:py-6 text-sm md:text-base font-space-mono font-bold
                    transition-all duration-200 whitespace-nowrap text-center
                    ${
                      index === activeIndex
                        ? "text-black dark:text-primary bg-theme-elevated dark:bg-theme-surface rounded-t-lg"
                        : "text-black dark:text-primary"
                    }
                  `}
                >
                  {product.name}
                </button>
              ))}
            </div>
          </Container>
        </div>
      )}

      {/* Active Product Content - Full Width Background */}
      <div className="w-full bg-theme-elevated dark:bg-theme-surface">
        <Container>
          {activeProduct && (
            <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center py-8 md:py-12 max-w-4xl mx-auto">
              {/* Feature image */}
              {featureImageSrc && (
                <div className="flex-1 w-full">
                  <Image
                    src={featureImageSrc}
                    alt={
                      activeProduct.feature_image?.alt ||
                      `${activeProduct.name} feature`
                    }
                    className="w-full h-auto"
                  />
                </div>
              )}

              {/* Title + content + CTA */}
              <div className="flex-1 flex flex-col gap-4 md:gap-6">
                <Text
                  size="base"
                  className="text-black dark:text-white leading-relaxed"
                >
                  {activeProduct.description}
                </Text>

                {activeProduct.feature_text && (
                  <Text
                    size="base"
                    className="font-space-mono text-black dark:text-white leading-relaxed"
                  >
                    {activeProduct.feature_text}
                  </Text>
                )}

                {cta_text && (
                  <div className="mt-2 md:mt-4">
                    <Button
                      href={cta_link?.cached_url || cta_link?.url}
                      variant="primary"
                      size="md"
                    >
                      {cta_text}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </Container>
      </div>
    </section>
  );
};

export default AppSuiteSection;
